import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { socket } from './socket';
import Loading from './components/Loading';

const Home = lazy(() => import('./components/Home'));
const Lobby = lazy(() => import('./components/Lobby'));
const GameRoom = lazy(() => import('./components/GameRoom'));
const RoomHandler = lazy(() => import('./components/RoomHandler'));
const Licensing = lazy(() => import('./components/Licensing'));
const Privacy = lazy(() => import('./components/Privacy'));
const GameDetails = lazy(() => import('./components/GameDetails'));
const GamesList = lazy(() => import('./components/GamesList'));
const Blog = lazy(() => import('./components/Blog'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const NotFound = lazy(() => import('./components/NotFound'));

function ConnectionBanner() {
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [hasError, setHasError] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
      setHasError(false);
      setIsReconnecting(false);
    }

    function onDisconnect() {
      setIsConnected(false);
      // Don't immediately show error, give reconnection a chance
      setIsReconnecting(true);
    }

    function onConnectError(err: Error) {
      console.error('Socket connection error:', err);
      setIsConnected(false);
      setHasError(true);
      setIsReconnecting(false);
    }

    function onReconnectAttempt() {
      setIsReconnecting(true);
      setHasError(false);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('reconnect_attempt', onReconnectAttempt);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('reconnect_attempt', onReconnectAttempt);
    };
  }, []);

  if (isConnected) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 p-2 text-center text-sm font-bold z-50 ${hasError ? 'bg-red-600 text-white' : 'bg-yellow-500 text-black'}`}>
      {hasError 
        ? "Connection Error: Unable to connect to game server. Retrying..." 
        : isReconnecting 
          ? "Reconnecting to server..." 
          : "Connecting to server..."}
    </div>
  );
}

function App() {
  useEffect(() => {
    // Connect only if not already connected
    // The auth callback in socket.ts will handle providing the userId
    if (!socket.connected) {
      socket.connect();
    }
    
    return () => {
      // Don't disconnect on unmount to allow for navigation
      // socket.disconnect();
    };
  }, []);

  return (
    <Router>
      <main className="min-h-dvh bg-ink text-wheat font-sans">
        <ConnectionBanner />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loading /></div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/games" element={<GamesList />} />
            <Route path="/games/:gameId" element={<GameDetails />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/lobby" element={<Lobby />} />
            <Route path="/game" element={<GameRoom />} />
            <Route path="/room/:roomId" element={<RoomHandler />} />
            <Route path="/licensing" element={<Licensing />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
    </Router>
  );
}

export default App;
